import { IProductLookup } from '../../../../../types/ComponentTypes/Components'
import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import productApi from '../../../../../api/productApi'
import { useApiClient } from '../../../../../context/AppContext'
import { useComponentContext } from '../../ComponentContext'
import useCurrentStore from '../../../../../hooks/useCurrentStore'
import ComponentSwitch from '../..'
import { Container } from '@sainsburys-tech/luna-grid'
import { ISearchProduct } from '../../../../../types'
import ProductSearchComponent from '../ProductSearchComponent'
import useProductLookup from './useProductLookup'
import ProductLookupInput from './ProductLookupInput'
import ProductField from './ProductField'
import ProductQuantityExceededModal from '../../../../Modal/ProductQuantityExceededModal/ProductQuantityExceededModal'

function ProductLookupV2Component({
  key,
  id,
  productName,
  price,
  quantity,
  putBackOnSale,
  productIsUnknown,
  defaultValue,
}: IProductLookup) {
  const apiClient = useApiClient()
  const { handleUpdate, handleGetAnswer } = useComponentContext()
  const storeLocationId = useCurrentStore().storeCode
  const handleUpdateRef = useRef(handleUpdate)
  const handleGetAnswerRef = useRef(handleGetAnswer)
  const resetStateRef = useRef<(() => void) | null>(null)

  // Keep refs up to date
  handleUpdateRef.current = handleUpdate
  handleGetAnswerRef.current = handleGetAnswer

  const [value, setValue] = useState(defaultValue ? JSON.parse(defaultValue) : null)
  const [searchProducts, setSearchProducts] = useState<ISearchProduct[]>([])
  const [searchMeta, setSearchMeta] = useState<{ currentPage: number; totalPages: number; query: string } | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [errorMessage, setErrorMessage] = useState('')
  const [isError, setIsError] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalStockOnHand, setModalStockOnHand] = useState<number | undefined>(undefined)
  const [modalEnteredQuantity, setModalEnteredQuantity] = useState<number | undefined>(undefined)

  const {
    lookupField,
    productNotFoundError,
    setLookupField,
    lookupProduct,
    resetState,
  } = useProductLookup(storeLocationId)
  
  // Store resetState in ref
  resetStateRef.current = resetState

  // Get the unknown product checkbox value
  const unknownCheckboxValue = handleGetAnswerRef.current({ id: productIsUnknown?.id })

  const isProductUnknownChecked = useMemo(
    () => unknownCheckboxValue === 'true' || (unknownCheckboxValue && unknownCheckboxValue.includes('true')),
    [unknownCheckboxValue]
  )

  const manualProductName = handleGetAnswerRef.current({ id: productName.id })
  const manualPrice = handleGetAnswerRef.current({ id: price.id })
  const manualQuantity = handleGetAnswerRef.current({ id: quantity.id })

  const resetSearch = useCallback(() => {
    setSearchProducts([])
    setSearchMeta(null)
  }, [])

  const setSearchFromResult = useCallback(
    (result: any, fallbackQuery: string) => {
      if (result?.products?.length > 0) {
        setSearchProducts(result.products)
        setSearchMeta({
          currentPage: result.currentPage,
          totalPages: result.totalPages,
          query: result.query || fallbackQuery,
        })
      } else {
        // Clear previous search state when there are no products or result is null/undefined
        resetSearch()
      }
    },
    [resetSearch]
  )

  // Track the previous mode to detect transitions
  const prevModeRef = useRef<'unknown' | 'search-result' | 'manual-entry' | 'initial' | null>(null)

  /**
   *  STATE SYNCHRONIZATION LOGIC
   * 
   * This effect manages all state transitions in one place to make the logic more explicit:
   * 
   * 1. UNKNOWN PRODUCT MODE: When checkbox is checked
   *    - Set default values (sku: '0', price: '0', quantity: '0')
   *    - Sync productName from manual input field
   *    - Clear search results
   * 
   * 2. SEARCH RESULT MODE: When user has selected a product from search
   *    - Keep the selected product data intact
   *    - Don't overwrite with manual input values
   * 
   * 3. MANUAL ENTRY MODE: When user manually types into fields (not from search)
   *    - Sync manual input fields (name, price, quantity) into value state
   *    - Only active when NOT in unknown mode and NOT from search
   * 
   * 4. INITIAL/RESET MODE: When checkbox is unchecked and no product selected
   *    - Clear all product data
   *    - Reset search state
   **/

  useEffect(() => {
    
    let currentMode: 'unknown' | 'search-result' | 'manual-entry' | 'initial'

    if (isProductUnknownChecked) {
      currentMode = 'unknown'
    } else if (value?.isFromSearch) {
      currentMode = 'search-result'
    } else if (manualProductName || manualPrice || manualQuantity) {
      currentMode = 'manual-entry'
    } else {
      currentMode = 'initial'
    }

    // TRANSITION: Moving to UNKNOWN PRODUCT mode
    if (isProductUnknownChecked && prevModeRef.current !== 'unknown') {
      setValue({ productName: manualProductName || '', sku: '0', price: '0', quantity: '0' })
      resetStateRef.current?.()
      resetSearch()
    }
    // TRANSITION: Leaving UNKNOWN PRODUCT mode
    else if (!isProductUnknownChecked && prevModeRef.current === 'unknown') {
      setValue(null)
      resetStateRef.current?.()
      resetSearch()
    }
    // UPDATE: In UNKNOWN PRODUCT mode, sync product name only
    else if (isProductUnknownChecked && value && manualProductName !== value.productName) {
      setValue((prev: any) => ({
        ...prev,
        productName: manualProductName
      }))
    }
    // UPDATE: In MANUAL ENTRY mode (not from search), sync all manual fields
    else if (currentMode === 'manual-entry' && !value?.isFromSearch) {
      setValue((prev: any) => {
        const nextValue = {
          productName: manualProductName || '',
          price: manualPrice || '',
          quantity: manualQuantity || '',
          isManualUserInput: false
        }
        // Only update if values actually changed to prevent unnecessary re-renders
        if (
          prev?.productName === nextValue.productName &&
          prev?.price === nextValue.price &&
          prev?.quantity === nextValue.quantity
        ) {
          return prev
        }
        return nextValue
      })
    }

    prevModeRef.current = currentMode
  }, [isProductUnknownChecked, manualProductName, manualPrice, manualQuantity, value, resetSearch])

  useEffect(() => {
    handleUpdateRef.current({ id, value: value == undefined ? value : JSON.stringify(value) })
  }, [value, id])

  /**
   * QUANTITY VALIDATION
   * 
   * Validate quantity when field loses focus (focusout) and show warning modal
   * if user enters a quantity that exceeds the available stock on hand.
   */
  const handleQuantityFocusOut = useCallback(() => {
    // Only validate for products with known stock that are from search results
    const shouldValidate = !isProductUnknownChecked 
      && value?.isFromSearch
      && value?.stockOnHand != null 
      && value.stockOnHand !== 0 
      && manualQuantity

    if (!shouldValidate) return

    const enteredQuantity = parseInt(manualQuantity, 10)
    const stockOnHand = value.stockOnHand
    
    // Show modal if quantity exceeds stock
    if (!isNaN(enteredQuantity) && enteredQuantity > stockOnHand) {
      setModalStockOnHand(stockOnHand)
      setModalEnteredQuantity(enteredQuantity)
      setIsModalOpen(true)
    }
  }, [manualQuantity, value?.stockOnHand, value?.isFromSearch, isProductUnknownChecked, value])

  const handleModalClose = () => {
    setIsModalOpen(false)
    setModalStockOnHand(undefined)
    setModalEnteredQuantity(undefined)
  }

  const handleLookupChange = (e: any) => {
    setLookupField(e.target.value)
  }

  const onScan = async (code: any) => {
    setLookupField(code)
    setIsLoading(true)
    try {
      const result = await lookupProduct(code)
      
      if (result?.products && result.products.length > 0) {
        // Auto-select first product when scanning
        handleProductSelected(result.products[0])
      } else {
        // Show error if no results
        setSearchFromResult(result, code)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleScanError = (msg: any) => {
    setErrorMessage(msg)
    setIsError(true)
  }

  const handleProductSelected = (product: ISearchProduct) => {
    const productInfo = {
      productName: product.productName,
      sku: product.sku || product.id,
      price: product.price,
      quantity: '',
      stockOnHand: product.stockOnHand,
      isManualUserInput: true,
      isFromSearch: true
    }
    setValue(productInfo)
    setLookupField('')
    
    // Clear manual input fields to prevent old manual data from persing
    handleUpdateRef.current({ id: productName.id, value: '' })
    handleUpdateRef.current({ id: price.id, value: '' })
  }

  const handleLoadMore = async () => {
    if (isLoadingMore || !searchMeta || searchMeta.currentPage >= searchMeta.totalPages) return
    
    setIsLoadingMore(true)
    const nextPage = searchMeta.currentPage + 1
    
    try {
      const response = await productApi.SearchProduct(apiClient, searchMeta.query, storeLocationId, nextPage)
      setSearchProducts(prev => [...prev, ...response.products])
      setSearchMeta({
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        query: response.query || searchMeta.query
      })
    } catch (error: any) {
      setErrorMessage('Unable to load more products. Please try again.')
      setIsError(true)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const hasProductDetails = Boolean(value?.productName && value?.isManualUserInput && !isProductUnknownChecked)
  const showSkuField = Boolean(value?.sku || (isProductUnknownChecked && !value?.isManualUserInput))
  const showPriceAsReadOnly = Boolean((value?.price && value?.isManualUserInput) || isProductUnknownChecked)

  const handleSearchClick = async () => {
    resetSearch()
    setIsLoading(true)
    try {
      const result = await lookupProduct(lookupField)
      setSearchFromResult(result, lookupField)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Container>

      <div className="ln-u-margin-bottom*2">
        <ComponentSwitch
          id={productIsUnknown?.id}
          type="checkboxList"
          options={[{
            value: 'true',
            label: "If you're unable to determine what products were taken, please provide a description in the \"Product name\" field and check this box."
          }]}
          defaultValue=""
          key={`${key}-unknown-product-checkbox`}
          error={productIsUnknown?.error}
        />
      </div>

      {!isProductUnknownChecked && (
        <>
          <ProductLookupInput
            lookupField={lookupField}
            productNotFoundError={productNotFoundError || (isError ? errorMessage : '')}
            onLookupChange={handleLookupChange}
            onSearchClick={handleSearchClick}
            onScan={onScan}
            onScanError={handleScanError}
          />
          <ProductSearchComponent
            products={searchProducts}
            hasMore={Boolean(searchMeta && searchMeta.currentPage < searchMeta.totalPages)}
            onSelect={handleProductSelected}
            onLoadMore={handleLoadMore}
            scrollThreshold={5}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
          />
        </>
      )}

      <div className='ln-u-margin-top*2'>
        <ProductField
          fieldKey={`${key}-product_name`}
          label="Product name (on packaging)"
          value={value?.productName}
          showAsReadOnly={hasProductDetails}
          inputId={productName.id}
          placeholder="Enter product info ..."
          answer={value?.productName}
          error={productName.error}
        />
      </div>

      {showSkuField && (
        <ProductField
          fieldKey={`${key}-sku`}
          label="SKU number"
          value={isProductUnknownChecked ? '0' : value?.sku}
          showAsReadOnly={true}
        />
      )}

      <ProductField
        fieldKey={`${key}-price`}
        label="Price"
        value={isProductUnknownChecked ? '£0.00' : `£${value?.price}`}
        showAsReadOnly={showPriceAsReadOnly}
        inputId={price.id}
        placeholder="Enter price ..."
        prefix="£"
        answer={value?.price}
        error={price.error}
      />

      <div onBlur={handleQuantityFocusOut}>
        <ComponentSwitch
          id={quantity.id}
          type="input"
          label="Quantity"
          placeholder="Enter quantity ..."
          answer={isProductUnknownChecked ? undefined : value?.quantity}
          key={`${key}-quantity`}
          prefix=""
          error={quantity.error}
        />
      </div>

      <ComponentSwitch
        id={putBackOnSale.id}
        type="checkboxList"
        options={[{
          value: 'true',
          label: 'Product was involved in incident, but was put back on sale'
        }]}
        defaultValue=""
        key={`${key}-product_put_back_on_sale`}
        error={putBackOnSale.error}
      />

      <ProductQuantityExceededModal
        open={isModalOpen}
        onContinue={handleModalClose}
        stockOnHand={modalStockOnHand}
        enteredQuantity={modalEnteredQuantity}
      />
      
    </Container>
  )
}

export default ProductLookupV2Component
